import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  exportUrl, PromoAccao, PromoDetail, PromoFilters, PromoGroup, PromoList, PromoMeta, PromoOpportunity,
  replenishmentPromotionsApi,
} from '../services/replenishmentPromotionsApi';
import GenerateNowButton from '../components/GenerateNowButton';
import { fmtGeneratedAt, fmtInt } from '../utils/replenishment';

const PAGE_SIZE = 100;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const FILTER_KEYS = ['snapshot', 'start_from', 'start_to', 'salesdeal', 'accao', 'estado_promo', 'cat2', 'search', 'apply_by',
  'alerta_compras', 'so_atrasadas', 'ordering'] as const;

const ACCAO_META: Record<PromoAccao, { label: string; badge: string }> = {
  d2_alta: { label: 'Passar a D2 — prioridade alta', badge: 'bg-danger' },
  d2_media: { label: 'Passar a D2 — prioridade média', badge: 'bg-warning text-dark' },
  ja_d2_reforcar: { label: 'Já D2 — reforçar stock nas lojas', badge: 'bg-primary' },
  nao_repor_t000: { label: 'Não repor — stock T000 insuficiente', badge: 'bg-dark' },
  manter_d1: { label: 'Manter D1', badge: 'bg-secondary' },
  ja_d2_ok: { label: 'Já D2 — ok', badge: 'bg-success' },
  volumoso_d1: { label: 'Volumoso — fica D1', badge: 'bg-light text-dark border' },
  sem_stock_t000: { label: 'Sem stock no T000', badge: 'bg-light text-dark border' },
  fora_universo: { label: 'Fora do universo da catalogação', badge: 'bg-light text-dark border' },
  a_terminar: { label: 'Promo a terminar — já não compensa repor', badge: 'bg-light text-dark border' },
};

const ESTADOS: Array<[string, string]> = [['', 'Todas'], ['a_comecar', 'A começar'], ['a_decorrer', 'A decorrer']];
const isOngoing = (r: { estado_promo?: string | null }): boolean => r.estado_promo === 'a_decorrer';
const ACCAO_ORDER = Object.keys(ACCAO_META) as PromoAccao[];

const ORDERINGS: Array<[string, string]> = [
  ['prioridade', 'Prioridade'], ['aplicar_ate', 'Prazo (aplicar até)'], ['inicio', 'Início da promo'],
  ['dpd_desc', 'Vendas/dia previstas'], ['falta_desc', 'Falta para compras'], ['desconto_desc', 'Desconto'],
  ['fim', 'Fim da promo'], ['dias_loja_desc', 'Dias de promo na loja'],
];

const lisbonToday = (): string => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Lisbon' });
const shiftDate = (iso: string, days: number): string => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
const fmtDate = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const fmtDateFull = (iso: string): string => iso.split('-').reverse().join('/');
const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const weekday = (iso: string): string => WEEKDAYS[new Date(`${iso}T12:00:00Z`).getUTCDay()];
const fmtDec = (v: number | null | undefined, digits = 1): string =>
  v === null || v === undefined ? '-' : v.toLocaleString('pt-PT', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const fmtEur = (v: number): string => v.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' });
const dealLabel = (deal: string | null): string => (deal ? deal : '(sem SALESDEAL)');

// Prazo: a reposição SAP corre às 06:30 do dia "aplicar até"
const deadlineTone = (iso: string, today: string): { badge: string; text: string } => {
  if (iso < today) return { badge: 'bg-danger', text: 'atrasado' };
  if (iso === today) return { badge: 'bg-danger', text: 'hoje' };
  if (iso === shiftDate(today, 1)) return { badge: 'bg-warning text-dark', text: 'amanhã' };
  return { badge: 'bg-light text-dark border', text: '' };
};

const DeadlineBadge: React.FC<{ iso: string; today: string }> = ({ iso, today }) => {
  const tone = deadlineTone(iso, today);
  return (
    <span className={`badge ${tone.badge}`} title="Aplicar no SAP antes das 06:30 deste dia">
      {weekday(iso)} {fmtDate(iso)}{tone.text && ` · ${tone.text}`}
    </span>
  );
};

const EstadoBadge: React.FC<{ row: PromoOpportunity }> = ({ row }) => (isOngoing(row)
  ? <span className="badge bg-info text-dark" title="A promoção já começou">a decorrer</span>
  : <span className="badge bg-light text-dark border">a começar</span>);

// Início e fim da mudança de preço (+ desde quando está em promo, numa descida adicional)
const PeriodCell: React.FC<{ row: PromoOpportunity }> = ({ row }) => (
  <div className="text-nowrap">
    <EstadoBadge row={row} /> {fmtDate(row.data_inicio)} → {fmtDate(row.data_fim)}
    <div className="text-muted">
      {row.inicio_promo && row.inicio_promo < row.data_inicio && <>em promo desde {fmtDate(row.inicio_promo)} · </>}
      {isOngoing(row) ? `faltam ${row.dias_promo} dias` : `${row.dias_promo} dias`}
      {row.dias_restantes_loja !== null && row.dias_restantes_loja !== undefined
        && <> · {row.dias_restantes_loja} na loja</>}
      {' · '}{row.condicao_promo || '-'}
    </div>
  </div>
);

const AccaoBadge: React.FC<{ row: PromoOpportunity }> = ({ row }) => (
  <>
    <span className={`badge ${ACCAO_META[row.accao].badge}`}>{ACCAO_META[row.accao].label}</span>
    {['nao_repor_t000', 'a_terminar'].includes(row.accao) && row.accao_desejada && (
      <div className="small text-muted">devia: {ACCAO_META[row.accao_desejada].label.toLowerCase()}</div>
    )}
  </>
);

const T000Cell: React.FC<{ row: PromoOpportunity }> = ({ row }) => (
  <div className="text-nowrap">
    <span title={`Procura online prevista ${fmtDec(row.procura_online_promo)} + envio às lojas ${row.envio_lojas}`}>
      {fmtInt(row.stock_T ?? 0)} {row.t_chega
        ? <span className="badge bg-success">chega</span>
        : <span className="badge bg-danger">não chega</span>}
    </span>
    {row.compra_T ? <div className="small text-muted">+{fmtInt(row.compra_T)} em compra</div> : null}
    {row.alerta_compras && (
      <div className="small text-danger fw-semibold" title="Procura total prevista acima de T000 + lojas + compra em curso">
        Compras: faltam {fmtInt(row.falta_promo)} un.
      </div>
    )}
  </div>
);

const DetailModal: React.FC<{ sku: string; snapshot?: string; onClose: () => void }> = ({ sku, snapshot, onClose }) => {
  const [data, setData] = useState<PromoDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    replenishmentPromotionsApi.getDetail(sku, snapshot)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e?.response?.data?.error || 'Erro ao carregar o detalhe'));
    return () => { alive = false; };
  }, [sku, snapshot]);

  return (
    <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,.4)' }} onClick={onClose}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{sku}{data?.promotions[0]?.nome ? ` — ${data.promotions[0].nome}` : ''}</h5>
            <button type="button" className="btn-close" aria-label="Fechar" onClick={onClose} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-warning">{error}</div>}
            {!data && !error && <div className="text-muted">A carregar…</div>}
            {data && (
              <>
                <h6>Promoções</h6>
                <div className="table-responsive mb-3">
                  <table className="table table-sm small">
                    <thead className="table-light">
                      <tr>
                        <th>Estado</th><th>Início</th><th>Fim</th><th>SALESDEAL</th><th className="text-end">Preço</th>
                        <th className="text-end">Vendas/dia (28d → prevista)</th><th>Base</th>
                        <th className="text-end">Procura online</th><th className="text-end">Envio lojas</th><th>Acção</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.promotions.map((p) => (
                        <tr key={`${p.data_inicio}-${p.salesdeal}`}>
                          <td><EstadoBadge row={p} />{isOngoing(p) && p.dias_decorridos ? (
                            <div className="text-muted">{p.q_promo_decorrida ?? 0} un. em {p.dias_decorridos} dias</div>) : null}</td>
                          <td>{fmtDateFull(p.data_inicio)}</td><td>{fmtDateFull(p.data_fim)}</td>
                          <td>{dealLabel(p.salesdeal)}</td>
                          <td className="text-end">{fmtEur(p.preco_actual)} → {fmtEur(p.preco_promo)} ({Math.round(p.desconto * 100)}%)</td>
                          <td className="text-end">{fmtDec(p.dpd28, 2)} → <strong>{fmtDec(p.dpd_previsto, 2)}</strong></td>
                          <td>{p.base_previsao === 'historico' ? `histórico (${p.dias_hist} dias)` : 'promoções semelhantes'}</td>
                          <td className="text-end">{fmtDec(p.procura_online_promo)}</td>
                          <td className="text-end">{p.envio_lojas}</td>
                          <td><AccaoBadge row={p} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <h6>Evolução da recomendação</h6>
                <table className="table table-sm small w-auto">
                  <thead className="table-light">
                    <tr><th>Snapshot</th><th>Início</th><th>Acção</th><th className="text-end">Prevista/dia</th><th className="text-end">Stock T000</th><th className="text-end">Falta</th></tr>
                  </thead>
                  <tbody>
                    {data.history.map((h) => (
                      <tr key={`${h.snapshot_date}-${h.data_inicio}-${h.salesdeal}`}>
                        <td>{fmtDateFull(h.snapshot_date)}</td><td>{fmtDateFull(h.data_inicio)}</td>
                        <td>{ACCAO_META[h.accao]?.label || h.accao}</td>
                        <td className="text-end">{fmtDec(h.dpd_previsto, 2)}</td>
                        <td className="text-end">{h.stock_T ?? '-'}</td>
                        <td className="text-end">{h.falta_promo || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ReplenishmentPromotionsPage: React.FC = () => {
  const today = useMemo(lisbonToday, []);
  const [params, setParams] = useSearchParams();

  const filters: PromoFilters = useMemo(() => {
    const f: Record<string, string> = {};
    FILTER_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v && (!['snapshot', 'start_from', 'start_to', 'apply_by'].includes(k) || ISO_DATE.test(v))) f[k] = v;
    });
    return f as PromoFilters;
  }, [params]);
  const page = Math.max(0, Number(params.get('page')) || 0);
  const grouped = params.get('grouped') !== '0';
  const detailSku = params.get('sku');

  const patchParams = useCallback((changes: Record<string, string>, push = false) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      if (!('page' in changes) && !('sku' in changes) && !('grouped' in changes)) next.delete('page');
      return next;
    }, { replace: !push });
  }, [setParams]);

  const [meta, setMeta] = useState<PromoMeta | null>(null);
  const [data, setData] = useState<PromoList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState(filters.search || '');

  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    replenishmentPromotionsApi.getMeta().then(setMeta).catch(() => undefined);
  }, [reloadTick]);

  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    replenishmentPromotionsApi.list(filters, PAGE_SIZE, page * PAGE_SIZE)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e?.response?.data?.error || 'Erro ao carregar as promoções'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, page, reloadTick]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchText !== (filters.search || '')) patchParams({ search: searchText });
    }, 400);
    return () => clearTimeout(t);
  }, [searchText]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedAccoes = useMemo(() => (filters.accao ? filters.accao.split(',') : []), [filters.accao]);
  const toggleAccao = (a: string) => {
    const next = selectedAccoes.includes(a) ? selectedAccoes.filter((x) => x !== a) : [...selectedAccoes, a];
    patchParams({ accao: next.join(',') });
  };
  const selectGroup = (g: PromoGroup) =>
    patchParams({ start_from: g.data_inicio, start_to: g.data_inicio, salesdeal: g.salesdeal || 'none' });

  const rows = useMemo(() => data?.results || [], [data]);
  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;
  const nextDeadline = data?.totals.aplicar_ate_min;
  const aReporTotal = (data?.counts_accao.d2_alta || 0) + (data?.counts_accao.d2_media || 0)
    + (data?.counts_accao.ja_d2_reforcar || 0);

  // Linhas com cabeçalho de grupo (data de início x SALESDEAL), na ordem em que aparecem
  const body = useMemo(() => {
    const out: Array<{ header?: string; row?: PromoOpportunity; key: string; group?: PromoGroup }> = [];
    let last = '';
    rows.forEach((r) => {
      const key = `${r.data_inicio}|${r.salesdeal || ''}`;
      if (grouped && key !== last) {
        last = key;
        out.push({
          header: `${isOngoing(r) ? 'A decorrer desde' : 'Começa'} ${weekday(r.data_inicio)} ${fmtDateFull(r.data_inicio)} · ${dealLabel(r.salesdeal)}`,
          key: `h-${key}`,
          group: data?.groups.find((g) => g.data_inicio === r.data_inicio && g.salesdeal === r.salesdeal),
        });
      }
      out.push({ row: r, key: `${key}|${r.sku}` });
    });
    return out;
  }, [rows, grouped, data]);

  return (
    <div className="pb-5">
      <div className="d-flex flex-wrap justify-content-between align-items-end mb-2 gap-2">
        <div>
          <h2 className="mb-0">🏷️ Reposições para promoções</h2>
          <div className="text-muted small">
            SKUs em promoção no Promoplan (preço abaixo do ZPR1) que justificam stock nas lojas: as que começam nos próximos
            45 dias (aplicar no SAP até às 06:30 do dia indicado, 2 dias úteis antes do início) e as que já estão a decorrer
            (aplicar no próximo dia útil). A promoção vai do início ao fim da mudança de preço. O Promoplan não tem códigos de
            campanha: as promoções agrupam-se por data de início e SALESDEAL.
          </div>
        </div>
        <div className="text-muted small text-end">
          {data?.snapshot && <>Snapshot de {fmtDateFull(data.snapshot)}<br /></>}
          <div data-testid="generated-at">{data?.calculated_at ? `Gerada em ${fmtGeneratedAt(data.calculated_at)}` : ''}</div>
          <div className="mt-1">
            <GenerateNowButton job="promo_store" onFinished={() => setReloadTick((t) => t + 1)} />
          </div>
        </div>
      </div>

      {/* KPIs */}
      {data && (
        <div className="row g-2 mb-3">
          <div className="col-6 col-md-3">
            <div className="card h-100"><div className="card-body py-2">
              <div className="text-muted small">Promoções (SKU × início)</div>
              <div className="fs-4 fw-semibold">{fmtInt(data.totals.n || 0)}</div>
              <div className="small text-muted">{fmtInt(data.totals.skus || 0)} SKUs · {fmtInt(data.totals.salesdeals || 0)} SALESDEAL</div>
              <div className="small text-muted">{fmtInt(data.totals.a_comecar || 0)} a começar · {fmtInt(data.totals.a_decorrer || 0)} a decorrer</div>
            </div></div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card h-100 border-primary"><div className="card-body py-2">
              <div className="text-muted small">A repor nas lojas</div>
              <div className="fs-4 fw-semibold text-primary">{fmtInt(aReporTotal)}</div>
              <div className="small text-muted">D2 alta/média + reforços</div>
            </div></div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card h-100 border-dark"><div className="card-body py-2">
              <div className="text-muted small">T000 insuficiente</div>
              <div className="fs-4 fw-semibold">{fmtInt(data.counts_accao.nao_repor_t000 || 0)}</div>
              <div className="small text-muted">{fmtInt(data.totals.alerta_compras || 0)} com alerta de compras</div>
            </div></div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card h-100"><div className="card-body py-2">
              <div className="text-muted small">Próximo prazo (aplicar até)</div>
              <div className="fs-4 fw-semibold">{nextDeadline ? <DeadlineBadge iso={nextDeadline} today={today} /> : '-'}</div>
            </div></div>
          </div>
        </div>
      )}

      {/* Acções */}
      {data && (
        <div className="d-flex flex-wrap gap-2 mb-3">
          {ACCAO_ORDER.map((a) => (
            <button key={a} type="button" aria-pressed={selectedAccoes.includes(a)}
              className={`btn btn-sm ${selectedAccoes.includes(a) ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => toggleAccao(a)}>
              {ACCAO_META[a].label} <span className="badge bg-secondary ms-1">{data.counts_accao[a] || 0}</span>
            </button>
          ))}
        </div>
      )}

      {/* Filtros */}
      <div className="card mb-3">
        <div className="card-body d-flex flex-wrap gap-3 align-items-end">
          <div>
            <span className="small text-muted d-block">Promoção</span>
            <div className="btn-group btn-group-sm" role="group" aria-label="Estado da promoção">
              {ESTADOS.map(([v, l]) => (
                <button key={v || 'all'} type="button" aria-pressed={(filters.estado_promo || '') === v}
                  className={`btn ${(filters.estado_promo || '') === v ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => patchParams({ estado_promo: v })}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="pr-from">Início de</label>
            <input id="pr-from" type="date" className="form-control form-control-sm" value={filters.start_from || ''}
              onChange={(e) => patchParams({ start_from: e.target.value })} />
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="pr-to">Início até</label>
            <input id="pr-to" type="date" className="form-control form-control-sm" value={filters.start_to || ''}
              onChange={(e) => patchParams({ start_to: e.target.value })} />
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="pr-apply">Aplicar até (≤)</label>
            <div className="input-group input-group-sm">
              <input id="pr-apply" type="date" className="form-control form-control-sm" value={filters.apply_by || ''}
                onChange={(e) => patchParams({ apply_by: e.target.value })} />
              <button type="button" className="btn btn-outline-secondary" onClick={() => patchParams({ apply_by: shiftDate(today, 1) })}>
                até amanhã
              </button>
            </div>
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="pr-deal">SALESDEAL</label>
            <select id="pr-deal" className="form-select form-select-sm" value={filters.salesdeal || ''}
              onChange={(e) => patchParams({ salesdeal: e.target.value })}>
              <option value="">Todos</option>
              {(meta?.salesdeals || []).map((d) => (
                <option key={d.salesdeal || 'none'} value={d.salesdeal || 'none'}>
                  {dealLabel(d.salesdeal)} · {fmtDate(d.data_inicio)} ({d.n})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="pr-cat2">Categoria 2</label>
            <select id="pr-cat2" className="form-select form-select-sm" value={filters.cat2 || ''}
              onChange={(e) => patchParams({ cat2: e.target.value })}>
              <option value="">Todas</option>
              {(meta?.cat2 || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="pr-search">Pesquisa</label>
            <input id="pr-search" type="search" className="form-control form-control-sm" placeholder="SKU ou nome"
              value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="pr-order">Ordenar</label>
            <select id="pr-order" className="form-select form-select-sm" value={filters.ordering || 'prioridade'}
              onChange={(e) => patchParams({ ordering: e.target.value === 'prioridade' ? '' : e.target.value })}>
              {ORDERINGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="form-check">
            <input id="pr-compras" className="form-check-input" type="checkbox" checked={filters.alerta_compras === '1'}
              onChange={(e) => patchParams({ alerta_compras: e.target.checked ? '1' : '' })} />
            <label className="form-check-label small" htmlFor="pr-compras">Só alerta compras</label>
          </div>
          <div className="form-check">
            <input id="pr-group" className="form-check-input" type="checkbox" checked={grouped}
              onChange={(e) => patchParams({ grouped: e.target.checked ? '' : '0' })} />
            <label className="form-check-label small" htmlFor="pr-group">Agrupar por início / SALESDEAL</label>
          </div>
          <div className="ms-auto d-flex gap-2 align-items-center">
            {loading && <div className="spinner-border spinner-border-sm text-primary" role="status" />}
            <button type="button" className="btn btn-sm btn-outline-secondary"
              onClick={() => { setSearchText(''); setParams({}, { replace: true }); }}>Limpar</button>
            <a className="btn btn-sm btn-success" href={exportUrl(filters)} download>⬇ Exportar CSV</a>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Grupos (data de início × SALESDEAL) */}
      {data && data.groups.length > 0 && (
        <div className="card mb-3">
          <div className="card-body">
            <h6 className="mb-2">Por data de início e SALESDEAL</h6>
            <div className="table-responsive" style={{ maxHeight: 260 }}>
              <table className="table table-sm table-hover small mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Início</th><th>SALESDEAL</th><th>Fim</th><th>Aplicar até</th><th className="text-end">SKUs</th>
                    <th className="text-end">A repor</th><th className="text-end">T000 insuf.</th>
                    <th className="text-end">Alerta compras</th>
                  </tr>
                </thead>
                <tbody>
                  {data.groups.map((g) => (
                    <tr key={`${g.data_inicio}|${g.salesdeal}`} style={{ cursor: 'pointer' }} onClick={() => selectGroup(g)}>
                      <td className="text-nowrap">{weekday(g.data_inicio)} {fmtDateFull(g.data_inicio)}
                        {g.estado_promo === 'a_decorrer' && <span className="badge bg-info text-dark ms-1">a decorrer</span>}</td>
                      <td>{dealLabel(g.salesdeal)}</td>
                      <td className="text-nowrap">{fmtDate(g.data_fim_min)}{g.data_fim_max !== g.data_fim_min && ` – ${fmtDate(g.data_fim_max)}`}</td>
                      <td><DeadlineBadge iso={g.aplicar_ate} today={today} /></td>
                      <td className="text-end">{g.n}</td>
                      <td className="text-end fw-semibold">{g.a_repor || '-'}</td>
                      <td className="text-end">{g.nao_repor || '-'}</td>
                      <td className="text-end">{g.alerta_compras ? `${g.alerta_compras} (${fmtInt(g.falta_un || 0)} un.)` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Lista */}
      {data && (
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between mb-2">
              <h6 className="mb-0">{fmtInt(data.count)} promoções</h6>
              <div className="small">
                <button type="button" className="btn btn-sm btn-outline-secondary me-1" disabled={page === 0}
                  onClick={() => patchParams({ page: String(page - 1 || '') }, true)}>‹</button>
                Página {page + 1} / {totalPages}
                <button type="button" className="btn btn-sm btn-outline-secondary ms-1" disabled={page + 1 >= totalPages}
                  onClick={() => patchParams({ page: String(page + 1) }, true)}>›</button>
              </div>
            </div>
            {rows.length === 0 ? <div className="text-muted">Sem promoções para estes filtros.</div> : (
              <div className="table-responsive">
                <table className="table table-sm table-hover small align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Acção</th><th>Aplicar até</th><th>SKU</th><th>Promoção (início → fim)</th><th className="text-end">Preço</th>
                      <th className="text-end">Vendas/dia</th><th className="text-end">Lojas c/ stock</th><th>T000</th>
                    </tr>
                  </thead>
                  <tbody>
                    {body.map((item) => item.header ? (
                      <tr key={item.key} className="table-secondary">
                        <td colSpan={8} className="fw-semibold">
                          {item.header}
                          {item.group && <span className="text-muted fw-normal"> · {item.group.n} SKUs · aplicar até <DeadlineBadge iso={item.group.aplicar_ate} today={today} /></span>}
                        </td>
                      </tr>
                    ) : (
                      <tr key={item.key}>
                        <td><AccaoBadge row={item.row!} /></td>
                        <td><DeadlineBadge iso={item.row!.aplicar_ate} today={today} /></td>
                        <td>
                          <button type="button" className="btn btn-link btn-sm p-0 text-start"
                            onClick={() => patchParams({ sku: item.row!.sku }, true)}>{item.row!.sku}</button>
                          <div className="text-muted" style={{ maxWidth: 320 }}>{item.row!.nome || '-'}</div>
                        </td>
                        <td><PeriodCell row={item.row!} /></td>
                        <td className="text-end text-nowrap">
                          {fmtEur(item.row!.preco_promo)}
                          <div className="text-muted">{Math.round(item.row!.desconto * 100)}% vs {fmtEur(item.row!.preco_actual)}</div>
                        </td>
                        <td className="text-end text-nowrap">
                          {fmtDec(item.row!.dpd28, 2)} → <strong>{fmtDec(item.row!.dpd_previsto, 2)}</strong>
                          <div className="text-muted">{item.row!.confianca === 'alta' ? 'confiança alta' : 'confiança baixa'}</div>
                          {isOngoing(item.row!) && (item.row!.dias_decorridos || 0) > 0 && (
                            <div className="text-muted" title="Unidades vendidas desde o início da promoção (até ontem)">
                              real: {item.row!.q_promo_decorrida ?? 0} un. / {item.row!.dias_decorridos} d
                            </div>)}
                        </td>
                        <td className="text-end" title={item.row!.stock_por_loja || ''}>{item.row!.lojas_com_stock ?? 0}</td>
                        <td><T000Cell row={item.row!} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {detailSku && (
        <DetailModal sku={detailSku} snapshot={filters.snapshot}
          onClose={() => patchParams({ sku: '' }, true)} />
      )}
    </div>
  );
};

export default ReplenishmentPromotionsPage;
